import {
  Button,
  CircularProgress,
  createStyles,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  makeStyles,
  Theme,
  Typography,
} from '@material-ui/core'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import { useFiles } from '../queries/files'
import { FileDetails } from '../../../../shared/src/data/fileDetails'
import { Alert, AlertTitle } from '@material-ui/lab'
import { useMergeFiles } from './mergeFiles'
import { ScanDetails } from '../../../../shared/src/data/scanDetails'
import React, { useEffect } from 'react'
import { useSelectedFilesContext } from '../../selectedFilesProvider'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    loading: {
      marginTop: theme.spacing(1),
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    alertContainer: {
      width: '100%',
    },
  }),
)

type FilesReorderProps = {
  selectedScan?: ScanDetails
  setIsStepValid: (isValid: boolean) => void
  fileDetails: FileDetails[]
}

export default function FilesReorder({
  selectedScan,
  setIsStepValid,
  fileDetails,
}: FilesReorderProps): JSX.Element {
  const classes = useStyles()

  const { files, setSelectedFiles } = useSelectedFilesContext()

  useEffect(() => {
    setIsStepValid(!!files.length)
  }, [setIsStepValid, files])

  const {
    data: fetchedFiles,
    isLoading: loadingFiles,
    error: fetchingFilesError,
    refetch,
  } = useFiles(fileDetails)
  useEffect(() => {
    if (!fetchedFiles?.length) {
      return
    }
    setSelectedFiles(fetchedFiles)
  }, [fetchedFiles, setSelectedFiles])

  const handleOnDragEnd = (result: any) => {
    if (!result.destination || !files) return

    const orderedFiles = Array.from(files)
    const [reorderedItem] = orderedFiles.splice(result.source.index, 1)
    orderedFiles.splice(result.destination.index, 0, reorderedItem)
    setSelectedFiles(orderedFiles)
  }

  const {
    mergeFiles: {
      refetch: mergeFetch,
      isLoading: mergeIsLoading,
      isError: mergeIsError,
    },
  } = useMergeFiles(selectedScan?.project.id, selectedScan?.id, files)

  if (loadingFiles || !files?.length) {
    return <CircularProgress className={classes.loading} color='secondary' />
  }

  if (fetchingFilesError) {
    return (
      <Alert
        severity='error'
        className={classes.alertContainer}
        action={
          <Button
            variant='contained'
            color='secondary'
            onClick={() => refetch()}
          >
            RETRY
          </Button>
        }
      >
        <AlertTitle>
          <Typography variant='h6'>Failed to get files</Typography>
        </AlertTitle>
        <Typography variant='subtitle1'>
          Unable to get files: "
          {(fetchingFilesError as Error)
            ? (fetchingFilesError as Error).message
            : 'Unknown error'}
          "
        </Typography>
      </Alert>
    )
  }

  return (
    <div>
      <DragDropContext onDragEnd={handleOnDragEnd}>
        <Droppable droppableId='droppable'>
          {(provided) => (
            <List
              {...provided.droppableProps}
              ref={provided.innerRef}
              disablePadding
              subheader={
                <ListSubheader disableGutters>
                  <Typography variant='h6'>Files related to scan</Typography>
                </ListSubheader>
              }
            >
              {files.map((f, index) => (
                <Draggable key={f.id} draggableId={f.id} index={index}>
                  {(provided) => (
                    <ListItem
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      ref={provided.innerRef}
                      disableGutters
                    >
                      <DragIndicatorIcon />
                      <ListItemText primary={f.name} />
                    </ListItem>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </List>
          )}
        </Droppable>
      </DragDropContext>
      {mergeIsLoading ? (
        <CircularProgress color='secondary' />
      ) : (
        fileDetails.length > 0 && (
          <Button
            variant='contained'
            color='secondary'
            onClick={() => mergeFetch()}
          >
            Download {fileDetails.length === 1 ? 'File' : 'Merged'}
          </Button>
        )
      )}
      {mergeIsError && (
        <Typography color='error'>Failed to download merged file</Typography>
      )}
    </div>
  )
}
